"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { downloadText } from "@/lib/tool-kit/download";
import { parseArms } from "@/lib/tool-kit/research-design/assignment";
import {
  buildDesign,
  carryoverBalance,
  type LatinDesign,
  MAX_CONDITIONS,
  positionBalanced,
  rowsForParticipants,
} from "@/lib/tool-kit/research-design/latin-square";
import { toCsv, toTsv } from "@/lib/tool-kit/research-design/matrix";
import {
  freshSeed,
  seededSource,
  shuffle,
} from "@/lib/tool-kit/research-design/random";

/** English strings. A translated content file overrides these through `ui`. */
export const LATIN_SQUARE_GENERATOR_STRINGS = {
  conditionsLabel: "Conditions — one per line or separated by commas",
  conditionsHint: "Between 2 and 26 conditions, each listed once.",
  defaultConditions: "Silence\nWhite noise\nInstrumental music\nLyrical music",
  designLegend: "Design",
  designBalanced: "Balanced (Williams)",
  designCyclic: "Simple cyclic",
  participantsLabel: "Number of participants",
  participantsPlaceholder: "24",
  defaultParticipants: "8",
  randomize: "Randomize letters and assignment",
  reset: "Back to the standard square",
  randomizedNote:
    "Randomized with seed {seed}: conditions were shuffled onto the letters, and the orders were shuffled within each cycle of participants. The balance is unchanged.",
  results: "Orders",
  prompt: "Enter at least two conditions.",
  tooMany: "Use at most {max} conditions.",
  duplicate: "List each condition once — {name} appears more than once.",
  conditionCount: "one: {count} condition|other: {count} conditions",
  orderCount: "one: {count} order|other: {count} orders",
  summaryEven: "{conditions}, {orders}: one balanced Latin square.",
  summaryOdd:
    "{conditions}, {orders}: for an odd number of conditions the Williams design uses two squares, the second being the first in reverse, to balance carryover.",
  summaryCyclic: "{conditions}, {orders}: one cyclic Latin square.",
  checkPositions: "Every condition appears equally often in every position.",
  checkCarryover:
    "one: Every condition is immediately followed by every other condition exactly once.|other: Every condition is immediately followed by every other condition exactly {count} times.",
  checkCarryoverFails:
    "First-order carryover is not balanced: {first} is always followed by {second}. Switch to the balanced design to fix it.",
  letterKey: "{letter} = {name}",
  colOrder: "Order",
  colCode: "Code",
  colPosition: "Position {n}",
  orderLabel: "Order {n}",
  copyOrders: "Copy orders",
  copied: "Copied!",
  assignmentHeading: "Participants",
  participantId: "P{n}",
  colParticipant: "Participant",
  colSequence: "Sequence",
  sequenceArrow: " → ",
  participantsPrompt:
    "Enter a number of participants to assign each one an order.",
  unevenNote:
    "{participants} is not a multiple of {orders}, so some orders are used once more than others. Recruit in multiples of {orders} for full balance.",
  participantCount: "one: {count} participant|other: {count} participants",
  copyAssignments: "Copy participant list",
  downloadCsv: "Download participant list (CSV)",
  csvFile: "latin-square-assignment.csv",
} as const;

type Strings = typeof LATIN_SQUARE_GENERATOR_STRINGS;

const MAX_PARTICIPANTS = 2000;

function letterOf(index: number): string {
  return String.fromCharCode(65 + index);
}

function firstDuplicate(names: readonly string[]): string | null {
  const seen = new Set<string>();
  for (const name of names) {
    const key = name.toLowerCase();
    if (seen.has(key)) {
      return name;
    }
    seen.add(key);
  }
  return null;
}

function summaryText(
  n: number,
  orders: number,
  design: LatinDesign,
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): string {
  const values = {
    conditions: tPlural(strings.conditionCount, locale, n, { count: fmt(n) }),
    orders: tPlural(strings.orderCount, locale, orders, { count: fmt(orders) }),
  };
  if (design === "cyclic") {
    return t(strings.summaryCyclic, values);
  }
  return t(n % 2 === 0 ? strings.summaryEven : strings.summaryOdd, values);
}

function Assignment({
  rows,
  orderNames,
  participants,
  strings,
}: {
  rows: readonly number[];
  orderNames: readonly (readonly string[])[];
  participants: number;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const width = Math.max(2, String(participants).length);
  const ids = rows.map((_, i) =>
    t(strings.participantId, { n: String(i + 1).padStart(width, "0") })
  );
  const positions = (orderNames[0] ?? []).map((_, i) =>
    t(strings.colPosition, { n: fmt(i + 1) })
  );
  const csvRows = [
    [strings.colParticipant, strings.colOrder, ...positions],
    ...rows.map((row, i) => [ids[i], row + 1, ...orderNames[row]]),
  ];
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.assignmentHeading}</h3>
      {participants % orderNames.length === 0 ? null : (
        <Notice>
          {t(strings.unevenNote, {
            participants: tPlural(
              strings.participantCount,
              locale,
              participants,
              {
                count: fmt(participants),
              }
            ),
            orders: fmt(orderNames.length),
          })}
        </Notice>
      )}
      <DataTable
        head={[strings.colParticipant, strings.colOrder, strings.colSequence]}
        rows={rows.map((row, i) => [
          ids[i],
          fmt(row + 1),
          orderNames[row].join(strings.sequenceArrow),
        ])}
      />
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyAssignments}
          text={toTsv(csvRows)}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(
              strings.csvFile,
              toCsv(csvRows),
              "text/csv;charset=utf-8"
            )
          }
          type="button"
        >
          {strings.downloadCsv}
        </button>
      </div>
    </div>
  );
}

export default function LatinSquareGeneratorWidget() {
  const strings = useToolUi(LATIN_SQUARE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [conditionsText, setConditionsText] = useState<string>(
    strings.defaultConditions
  );
  const [design, setDesign] = useState<LatinDesign>("balanced");
  const [participantsText, setParticipantsText] = useState<string>(
    strings.defaultParticipants
  );
  const [seed, setSeed] = useState<string | null>(null);

  const names = parseArms(conditionsText);
  const n = names.length;
  const duplicate = firstDuplicate(names);
  const valid = n >= 2 && n <= MAX_CONDITIONS && duplicate === null;
  const letterRows = valid ? buildDesign(n, design) : [];
  const next = seed ? seededSource(seed) : null;
  const identity = names.map((_, i) => i);
  const letterToCondition = next ? shuffle(identity, next) : identity;
  const orderNames = letterRows.map((row) =>
    row.map((letter) => names[letterToCondition[letter]])
  );
  const participants = parseIntegerInput(participantsText);
  const assigned =
    valid && participants !== null && participants > 0
      ? rowsForParticipants(
          Math.min(participants, MAX_PARTICIPANTS),
          letterRows.length,
          next ?? undefined
        )
      : [];
  const carryover = valid ? carryoverBalance(letterRows, n) : null;
  const positions = names.map((_, i) =>
    t(strings.colPosition, { n: fmt(i + 1) })
  );
  const orderTable = [
    [strings.colOrder, strings.colCode, ...positions],
    ...orderNames.map((row, i) => [
      t(strings.orderLabel, { n: fmt(i + 1) }),
      letterRows[i].map(letterOf).join(""),
      ...row,
    ]),
  ];

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          hint={strings.conditionsHint}
          id="ls-conditions"
          label={strings.conditionsLabel}
          onChange={setConditionsText}
          prose
          rows={5}
          value={conditionsText}
        />
        <PillGroup
          label={strings.designLegend}
          name="ls-design"
          onChange={setDesign}
          options={[
            { value: "balanced", label: strings.designBalanced },
            { value: "cyclic", label: strings.designCyclic },
          ]}
          value={design}
        />
        <div className="max-w-xs">
          <TextField
            id="ls-participants"
            label={strings.participantsLabel}
            numeric
            onChange={setParticipantsText}
            placeholder={strings.participantsPlaceholder}
            value={participantsText}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={!valid}
            onClick={() => setSeed(freshSeed())}
            type="button"
          >
            {strings.randomize}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={seed === null}
            onClick={() => setSeed(null)}
            type="button"
          >
            {strings.reset}
          </button>
        </div>
      </section>

      <section aria-live="polite" className="space-y-6">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {n < 2 ? <Notice>{strings.prompt}</Notice> : null}
        {n > MAX_CONDITIONS ? (
          <ErrorNotice>
            {t(strings.tooMany, { max: fmt(MAX_CONDITIONS) })}
          </ErrorNotice>
        ) : null}
        {duplicate ? (
          <ErrorNotice>{t(strings.duplicate, { name: duplicate })}</ErrorNotice>
        ) : null}
        {valid ? (
          <>
            <p className="text-foreground text-sm">
              {summaryText(n, letterRows.length, design, strings, locale, fmt)}
            </p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
              {positionBalanced(letterRows, n) ? (
                <li>{strings.checkPositions}</li>
              ) : null}
              <li>
                {carryover === null
                  ? t(strings.checkCarryoverFails, {
                      first: names[letterToCondition[0]],
                      second: names[letterToCondition[1]],
                    })
                  : tPlural(strings.checkCarryover, locale, carryover, {
                      count: fmt(carryover),
                    })}
              </li>
            </ul>
            {seed ? (
              <Notice>{t(strings.randomizedNote, { seed })}</Notice>
            ) : null}
            <DataTable head={orderTable[0]} rows={orderTable.slice(1)} />
            <p className={HINT}>
              {letterToCondition
                .map((condition, letter) =>
                  t(strings.letterKey, {
                    letter: letterOf(letter),
                    name: names[condition],
                  })
                )
                .join("; ")}
            </p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyOrders}
              text={toTsv(orderTable)}
            />
            {assigned.length > 0 ? (
              <Assignment
                orderNames={orderNames}
                participants={assigned.length}
                rows={assigned}
                strings={strings}
              />
            ) : (
              <Notice>{strings.participantsPrompt}</Notice>
            )}
          </>
        ) : null}
      </section>
    </>
  );
}
